import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";

const MultiSelectCheckboxDropdown = ({
  options = [],
  selectedValues = [],
  placeholder = "Select",
  onChange = () => {},
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (val) => {
    let updated;
    if (selectedValues.includes(val)) {
      updated = selectedValues.filter(v => v !== val);
    } else {
      updated = [...selectedValues, val];
    }
    onChange(updated);
  };

  const getDisplayText = () => {
    if (!selectedValues || selectedValues.length === 0) return "";
    const selectedLabels = options
      .filter(opt => selectedValues.includes(opt.value))
      .map(opt => opt.label);
    return selectedLabels.join(", ");
  };

  const displayText = getDisplayText();

  return (
    <Container ref={dropdownRef} className={disabled ? "disabled" : ""}>
      <ControlBox onClick={() => !disabled && setIsOpen(!isOpen)}>
        <ValueText className={!displayText ? "is-placeholder" : ""} title={displayText}>
          {displayText || placeholder}
        </ValueText>
        <ArrowIcon className={isOpen ? "open" : ""}>▼</ArrowIcon>
      </ControlBox>

      {isOpen && !disabled && (
        <Menu>
          {options.map((opt) => {
            const isChecked = selectedValues.includes(opt.value);
            return (
              <MenuItem key={opt.value} onClick={() => toggleOption(opt.value)}>
                <CustomCheckboxBox className={isChecked ? "checked" : ""}>
                  {isChecked && <CheckMark>✓</CheckMark>}
                </CustomCheckboxBox>
                <span>{opt.label}</span>
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </Container>
  );
};

export default MultiSelectCheckboxDropdown;

const Container = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;

  &.disabled > div {
    background-color: #f8fafc !important;
    border-color: #e2e8f0 !important;
    cursor: not-allowed;
  }
`;

const ControlBox = styled.div`
  border: 1px solid #d1d5db !important;
  background-color: #ffffff !important;
  border-radius: 6px;
  height: 38px;
  padding: 0 28px 0 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;

  &:hover {
    border-color: #94a3b8 !important;
  }
`;

const ValueText = styled.div`
  font-size: 13px;
  color: #1e293b;
  font-weight: 500;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: transparent !important;
  text-align: left;

  &.is-placeholder {
    color: #94a3b8 !important;
    font-weight: normal !important;
    background: transparent !important;
  }
`;

const ArrowIcon = styled.span`
  font-size: 10px;
  color: #64748b;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  transition: transform 0.2s ease;
  pointer-events: none;

  &.open {
    transform: translateY(-50%) rotate(180deg);
  }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 999;
  max-height: 180px;
  overflow-y: auto;
  padding: 4px 0;
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  font-size: 13px;
  color: #334155;
  cursor: pointer;
  user-select: none;
  &:hover {
    background-color: #f1f5f9;
  }
`;

const CustomCheckboxBox = styled.div`
  width: 16px;
  height: 16px;
  min-width: 16px;
  max-width: 16px;
  min-height: 16px;
  max-height: 16px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  transition: all 0.15s ease;
  box-sizing: border-box;

  &.checked {
    background-color: #28a745;
    border-color: #28a745;
  }
`;

const CheckMark = styled.span`
  color: #ffffff;
  font-size: 11px;
  font-weight: bold;
  line-height: 1;
`;
